import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BedBadge, FreshnessBadge } from "@/components/FacilityCard";
import { InquiryDialog } from "@/components/InquiryDialog";
import { AuthDialog } from "@/components/AuthDialog";
import { ClaimDialog } from "@/components/ClaimDialog";
import { MapView } from "@/components/MapView";
import { MapErrorBoundary } from "@/components/MapErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { categoryLabel } from "@/lib/categories";
import { api } from "@/lib/api";
import { track } from "@/lib/track";
import { useSeo } from "@/lib/seo";
import {
  Heart, MapPin, Phone, BadgeCheck, MessageSquare, Building2, ShieldCheck, Globe, ChevronLeft,
} from "lucide-react";

const FALLBACKS = [
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1631679706909-1844bbd07221?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
];

export default function FacilityPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { settings } = useSettings();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [isFav, setIsFav] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true); setNotFound(false);
    api.get(`/facilities/${id}`)
      .then(({ data }) => { if (active) { setFacility(data); track("facility_view", { facility_id: data.facility_id }); } })
      .catch(() => { if (active) setNotFound(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  useEffect(() => { setIsFav((user?.favorites || []).includes(id)); }, [user, id]);

  const brand = settings?.brand_name || "CareBed";
  const cityState = facility ? [facility.city, facility.state].filter(Boolean).join(", ") : "";
  const typeLabel = facility ? categoryLabel(settings, facility.category, facility.facility_type) : "";
  useSeo(facility ? {
    title: `${facility.name}${cityState ? ` — ${cityState}` : ""} | ${brand}`,
    description: `${facility.name} is a ${typeLabel.toLowerCase()}${cityState ? ` in ${cityState}` : ""} with ${facility.licensed_beds_total || 0} licensed beds. See availability, amenities, payment types, and contact information on ${brand}.`,
    canonical: `${window.location.origin}/facility/${id}`,
    image: facility.photos?.[0],
  } : { title: `Facility | ${brand}` });

  const handleFav = async () => {
    if (!user) { setAuthOpen(true); return; }
    const { data } = await api.post(`/favorites/${id}`);
    setIsFav(data.favorited);
    setUser((u) => u ? { ...u, favorites: data.favorites } : u);
    track("favorite", { facility_id: id });
  };
  const handleInquiry = () => {
    track("inquiry_click", { facility_id: id });
    if (!user) { setAuthOpen(true); return; }
    setInquiryOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]"><Header />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-4">
          <Skeleton className="h-72 w-full rounded-2xl" />
          <Skeleton className="h-10 w-2/3 rounded-xl" />
          <Skeleton className="h-40 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (notFound || !facility) {
    return (
      <div className="min-h-screen bg-[#FAF9F6]"><Header />
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-24 text-center">
          <h1 className="font-heading text-3xl font-medium text-[#1C211F] mb-3">Facility not found</h1>
          <p className="text-[#5C6660] mb-6">This listing may have been removed or the link is incorrect.</p>
          <Link to="/search"><Button className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl" data-testid="notfound-search-btn">Browse facilities</Button></Link>
        </div>
      </div>
    );
  }

  const images = facility.photos?.length ? facility.photos : [FALLBACKS[(facility.licensed_beds_total || 0) % 2]];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="facility-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#4A534E] hover:text-[#1A365D] mb-4" data-testid="facility-back-btn">
          <ChevronLeft className="h-4 w-4" /> Back to results
        </button>

        <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden mb-6">
          <img src={images[0]} alt={facility.name} className="w-full h-full object-cover" />
          {facility.claimed && (
            <span className="absolute bottom-4 left-4 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium bg-white/90 text-[#1A365D]"><BadgeCheck className="h-4 w-4" /> Verified listing</span>
          )}
        </div>

        <p className="text-xs uppercase tracking-wide text-[#5C6660] font-medium mb-1">{typeLabel}{facility.facility_type_detail ? ` · ${facility.facility_type_detail}` : ""}</p>
        <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1C211F] leading-tight mb-3" data-testid="facility-name">{facility.name}</h1>

        <div className="flex items-center gap-3 flex-wrap mb-6">
          {facility.tier === "premium" && (
            <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-[#0077B6] text-white">Featured</span>
          )}
          <BedBadge beds={facility.available_beds} tier={facility.tier} />
          <span className="text-sm text-[#5C6660]">·</span>
          <span className="text-sm text-[#4A534E] font-mono-data">{facility.licensed_beds_total} licensed beds</span>
          <FreshnessBadge facility={facility} />
        </div>

        <div className="flex gap-3 mb-8 max-w-md">
          {facility.inquiries_on ? (
            <Button onClick={handleInquiry} data-testid="contact-inquiry-btn" className="flex-1 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12">
              <MessageSquare className="h-4 w-4 mr-2" /> Contact facility
            </Button>
          ) : (
            <a href={`tel:${facility.telephone}`} onClick={() => track("phone_click", { facility_id: facility.facility_id })} className="flex-1" data-testid="call-facility-btn">
              <Button className="w-full bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12">
                <Phone className="h-4 w-4 mr-2" /> Call to inquire
              </Button>
            </a>
          )}
          <Button onClick={handleFav} variant="outline" data-testid="facility-favorite-btn" aria-label={isFav ? "Remove from saved facilities" : "Save this facility"} aria-pressed={isFav} className="rounded-xl h-12 w-12 border-[#E8E6DF] p-0">
            <Heart className={`h-5 w-5 ${isFav ? "fill-[#0077B6] text-[#0077B6]" : "text-[#4A534E]"}`} />
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3"><MapPin className="h-5 w-5 text-[#5C6660] mt-0.5" /><div><div className="text-sm text-[#1C211F]">{facility.address_full}</div><div className="text-xs text-[#5C6660]">{facility.county} County</div></div></div>
              <a href={`tel:${facility.telephone}`} onClick={() => track("phone_click", { facility_id: facility.facility_id })} className="flex items-center gap-3 text-[#1C211F] hover:text-[#1A365D]"><Phone className="h-5 w-5 text-[#5C6660]" /><span className="text-sm">{facility.telephone}</span></a>
              <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-[#5C6660]" /><span className="text-sm text-[#1C211F]">CDPHE {facility.cdphe_licensed} · {facility.operating_status}</span></div>
              {facility.tier === "premium" && facility.website && (
                <a href={facility.website.startsWith("http") ? facility.website : `https://${facility.website}`} target="_blank" rel="noopener noreferrer" data-testid="facility-website-link" className="flex items-center gap-3 text-[#0077B6] hover:underline">
                  <Globe className="h-5 w-5 text-[#0077B6]" /><span className="text-sm font-medium">Visit website</span>
                </a>
              )}
            </div>

            {facility.description && (
              <div className="border-t border-[#E8E6DF] pt-5">
                <h2 className="font-heading text-lg font-medium mb-2">About {facility.name}</h2>
                <p className="text-[#4A534E] leading-relaxed text-sm">{facility.description}</p>
              </div>
            )}

            {facility.amenities?.length > 0 && (
              <div className="border-t border-[#E8E6DF] pt-5">
                <h2 className="font-heading text-lg font-medium mb-3">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {facility.amenities.map((a) => <span key={a} className="px-3 py-1.5 rounded-full bg-[#EFEEE8] text-sm text-[#2A312D]">{a}</span>)}
                </div>
              </div>
            )}

            {facility.payment_types?.length > 0 && (
              <div className="border-t border-[#E8E6DF] pt-5">
                <h2 className="font-heading text-lg font-medium mb-3">Payment types accepted</h2>
                <div className="flex flex-wrap gap-2">
                  {facility.payment_types.map((p) => <span key={p} className="px-3 py-1.5 rounded-full bg-[#E3F3ED] text-sm text-[#1A365D] font-medium">{p}</span>)}
                </div>
              </div>
            )}
          </div>

          <div>
            {typeof facility.lat === "number" && (
              <div className="h-72 rounded-2xl overflow-hidden border border-[#E8E6DF]">
                <MapErrorBoundary>
                  <MapView facilities={[facility]} activeId={facility.facility_id} center={{ lat: facility.lat, lng: facility.lng }} onSelect={() => {}} onHover={() => {}} />
                </MapErrorBoundary>
              </div>
            )}
          </div>
        </div>

        {!facility.claimed && (
          <div className="mt-10 bg-[#E3F3ED] rounded-2xl p-6">
            <h2 className="font-heading text-lg font-medium flex items-center gap-2 mb-1"><Building2 className="h-5 w-5" /> Is this your facility?</h2>
            <p className="text-sm text-[#4A534E] mb-3">Claim this listing to manage your profile, add photos and amenities, and keep bed availability current. We'll verify your request before approving.</p>
            <Button onClick={() => setClaimOpen(true)} data-testid="claim-btn" className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl">Claim this listing</Button>
          </div>
        )}
      </div>
      <Footer />

      <InquiryDialog facility={facility} open={inquiryOpen} onOpenChange={setInquiryOpen} user={user} />
      <ClaimDialog facility={facility} open={claimOpen} onOpenChange={setClaimOpen} user={user} />
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}
